/**
 * @fileoverview Camada de gamificação do onboarding: ao concluir uma tarefa
 * (evento vindo do OnboardingContext), dispara confetti (canvas-confetti) e um
 * toast de parabéns que SOBE de baixo para cima (entrada e saída suaves).
 * Variante maior para o AHA (criar IA) e para a conclusão das obrigatórias.
 * Paleta e tom alinhados ao projeto (azul de marca, sem canvas persistente).
 */

import React, { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { makeStyles } from "@material-ui/core";
import { PartyPopper, Sparkles, Trophy } from "lucide-react";

import { i18n } from "../../../translate/i18n";
import { useOnboarding } from "../../../context/Onboarding/OnboardingContext";

const useStyles = makeStyles(() => ({
  wrap: {
    position: "fixed",
    bottom: 32,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 2100,
    pointerEvents: "none",
    display: "flex",
    justifyContent: "center"
  },
  toast: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "15px 24px",
    borderRadius: 14,
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: 700,
    fontSize: "0.95rem",
    lineHeight: 1.35,
    color: "#fff",
    // Azul de marca (sem o roxo anterior).
    background: "linear-gradient(135deg, #4F7CFF 0%, #3D5CFD 100%)",
    border: "1px solid rgba(255,255,255,0.14)",
    boxShadow: "0 14px 44px rgba(61,92,253,0.42), 0 2px 8px rgba(0,0,0,0.18)",
    maxWidth: "min(90vw, 560px)",
    // Entrada: sobe de baixo, com fade. `both` mantém o estado inicial (escondido).
    animation: "$slideUp 540ms cubic-bezier(0.16,1,0.3,1) both"
  },
  // Saída: desce e some (aplicada no fim, antes de desmontar).
  leaving: {
    animation: "$slideDown 420ms cubic-bezier(0.4,0,1,1) forwards"
  },
  iconWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: 30,
    height: 30,
    borderRadius: 9,
    background: "rgba(255,255,255,0.16)"
  },
  "@keyframes slideUp": {
    "0%": { opacity: 0, transform: "translateY(48px)" },
    "100%": { opacity: 1, transform: "translateY(0)" }
  },
  "@keyframes slideDown": {
    "0%": { opacity: 1, transform: "translateY(0)" },
    "100%": { opacity: 0, transform: "translateY(48px)" }
  }
}));

// Confete com paleta da marca (azuis + branco + um dourado de acento) — evita o
// arco-íris padrão, deixando a comemoração com cara de produto.
const CONFETTI_COLORS = ["#3D5CFD", "#5B7CFF", "#7B9CFF", "#FFFFFF", "#FFC94D"];

const fireConfetti = big => {
  const base = {
    spread: 75,
    startVelocity: 45,
    ticks: 200,
    zIndex: 2200,
    colors: CONFETTI_COLORS,
    disableForReducedMotion: true
  };
  confetti({ ...base, particleCount: big ? 150 : 70, origin: { x: 0.5, y: 0.35 } });
  if (big) {
    // Jatos laterais subindo das bordas inferiores (combina com o toast que sobe).
    confetti({ ...base, particleCount: 70, angle: 60, origin: { x: 0, y: 0.75 } });
    confetti({ ...base, particleCount: 70, angle: 120, origin: { x: 1, y: 0.75 } });
  }
};

const Celebration = () => {
  const classes = useStyles();
  const { celebration, clearCelebration } = useOnboarding();
  const [shown, setShown] = useState(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!celebration) return undefined;
    const big = celebration.ahaDone || celebration.allRequiredDone;
    fireConfetti(big);
    setShown(celebration);
    setLeaving(false);

    // Tempo visível generoso (perceptível) + saída suave antes de desmontar.
    const visibleMs = big ? 4800 : 3000;
    const exitMs = 440;
    const tLeave = setTimeout(() => setLeaving(true), visibleMs);
    const tEnd = setTimeout(() => {
      setShown(null);
      setLeaving(false);
      clearCelebration();
    }, visibleMs + exitMs);

    return () => {
      clearTimeout(tLeave);
      clearTimeout(tEnd);
    };
  }, [celebration, clearCelebration]);

  if (!shown) return null;

  let messageKey = "taskDone";
  let Icon = PartyPopper;
  if (shown.allRequiredDone) {
    messageKey = "allDone";
    Icon = Trophy;
  } else if (shown.ahaDone) {
    messageKey = "aha";
    Icon = Sparkles;
  }

  return (
    <div className={classes.wrap}>
      <div className={`${classes.toast} ${leaving ? classes.leaving : ""}`}>
        <span className={classes.iconWrap}>
          <Icon size={18} strokeWidth={2} />
        </span>
        {i18n.t(`onboarding.celebration.${messageKey}`)}
      </div>
    </div>
  );
};

export default Celebration;
